import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders(): Record<string, string> {
  try {
    const token = (JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state ?? {})?.token ?? '';
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
  } catch {
    return { 'Content-Type': 'application/json' };
  }
}

function fmtUGX(n: number): string {
  return `UGX ${Math.round(n).toLocaleString('en-UG')}`;
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-UG', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Customer {
  id: string;
  fullName: string;
  phone: string;
  email: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  preferredLanguage: string;
  notes: string | null;
  loyaltyPoints: number;
  loyaltyTier: 'classic' | 'silver' | 'gold' | 'platinum';
  totalSpendUgx: number;
  visitCount: number;
  lastVisitDate: string | null;
  isBlacklisted: boolean;
  marketingOptIn: boolean;
}

interface OrderSummary {
  id: string;
  orderNumber?: string;
  totalAmount: number;
  createdAt: string;
  status: string;
}

interface LoyaltyTx {
  id: string;
  points: number;
  type: string;
  description?: string;
  createdAt: string;
}

interface CustomerHistory {
  customer: Customer;
  orders: OrderSummary[];
  loyaltyTransactions: LoyaltyTx[];
}

interface Campaign {
  id: string;
  name: string;
  messageTemplate: string;
  campaignType: string;
  status: 'draft' | 'sent' | 'failed';
  sentAt: string | null;
  recipientCount: number;
  deliveredCount: number;
  failedCount: number;
}

// ─── Tier badge helper ────────────────────────────────────────────────────────

function tierBadgeClass(tier: Customer['loyaltyTier']): string {
  if (tier === 'gold') return 'badge badge-amber';
  if (tier === 'platinum') return 'badge badge-purple';
  if (tier === 'silver') return 'badge badge-blue';
  return 'badge badge-gray';
}

// ─── Customer modal (add / edit) ──────────────────────────────────────────────

function CustomerModal({
  initial,
  onClose,
  onSave,
  isSaving,
}: {
  initial?: Partial<Customer>;
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
  isSaving: boolean;
}) {
  const [fullName, setFullName] = useState(initial?.fullName ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(
    initial?.dateOfBirth ? initial.dateOfBirth.slice(0, 10) : '',
  );
  const [gender, setGender] = useState(initial?.gender ?? '');
  const [marketingOptIn, setMarketingOptIn] = useState(initial?.marketingOptIn ?? false);
  const [notes, setNotes] = useState(initial?.notes ?? '');

  const isEdit = Boolean(initial?.id);
  const canSave = fullName.trim().length > 0 && phone.trim().length > 0;

  function handleSave() {
    if (!canSave) return;
    const payload: Record<string, unknown> = {
      fullName: fullName.trim(),
      phone: phone.trim(),
      marketingOptIn,
    };
    if (email.trim()) payload['email'] = email.trim();
    if (dateOfBirth) payload['dateOfBirth'] = dateOfBirth;
    if (gender) payload['gender'] = gender;
    if (notes.trim()) payload['notes'] = notes.trim();
    onSave(payload);
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2 className="modal-title">{isEdit ? 'Edit Customer' : 'New Customer'}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Full Name *</label>
            <input
              className="input"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="e.g. Amara Nakato"
            />
          </div>
          <div className="field">
            <label className="field-label">Phone *</label>
            <input
              className="input"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+256 7XX XXX XXX"
            />
          </div>
          <div className="field">
            <label className="field-label">Email</label>
            <input
              className="input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="optional"
            />
          </div>
          <div className="field">
            <label className="field-label">Date of Birth</label>
            <input
              className="input"
              type="date"
              value={dateOfBirth}
              onChange={e => setDateOfBirth(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label">Gender</label>
            <select className="input" value={gender} onChange={e => setGender(e.target.value)}>
              <option value="">Not specified</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Notes</label>
            <textarea
              className="input"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional notes"
              style={{ resize: 'none' }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <div
                onClick={() => setMarketingOptIn(v => !v)}
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  width: 36,
                  height: 20,
                  alignItems: 'center',
                  borderRadius: 999,
                  background: marketingOptIn ? 'var(--accent)' : 'var(--surface2)',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    transform: marketingOptIn ? 'translateX(18px)' : 'translateX(3px)',
                    transition: 'transform 0.2s',
                  }}
                />
              </div>
              <span style={{ fontSize: 13, color: 'var(--text-2)' }}>Marketing opt-in (SMS/promotions)</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={!canSave || isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Customer history panel ────────────────────────────────────────────────────

function CustomerHistoryPanel({
  customerId,
  onClose,
}: {
  customerId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery<CustomerHistory>({
    queryKey: ['customer-history', customerId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/crm/customers/${customerId}/history`, {
        headers: authHeaders(),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data;
    },
  });

  const customer = data?.customer;
  const orders = data?.orders ?? [];
  const loyaltyTxs = data?.loyaltyTransactions ?? [];

  return (
    <div className="modal-overlay">
      <div
        style={{
          background: 'var(--surface)',
          width: '100%',
          maxWidth: 480,
          height: '100%',
          maxHeight: '90vh',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Customer History</h2>
            {customer && (
              <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{customer.phone}</p>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {isLoading ? (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} style={{ height: 40, background: 'var(--surface2)', borderRadius: 8, animation: 'pulse 1.5s infinite' }} />
            ))}
          </div>
        ) : customer ? (
          <div style={{ overflowY: 'auto', flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Stats strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {[
                { label: 'Points', value: customer.loyaltyPoints.toLocaleString() },
                { label: 'Visits', value: String(customer.visitCount) },
                { label: 'Total Spend', value: fmtUGX(customer.totalSpendUgx) },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--surface2)', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                  <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{s.label}</p>
                  <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Tier & flags */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span className={tierBadgeClass(customer.loyaltyTier)}>{customer.loyaltyTier.toUpperCase()}</span>
              {customer.isBlacklisted && <span className="badge badge-red">Blacklisted</span>}
              {customer.marketingOptIn && <span className="badge badge-green">Marketing OK</span>}
              {customer.email && (
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{customer.email}</span>
              )}
            </div>

            {/* Orders */}
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                Orders ({orders.length})
              </p>
              {orders.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No orders yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {orders.map((o, idx) => (
                    <div
                      key={o.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingTop: 10,
                        paddingBottom: 10,
                        borderBottom: idx < orders.length - 1 ? '1px solid var(--border)' : 'none',
                      }}
                    >
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                          {o.orderNumber ?? o.id.slice(0, 8)}
                        </p>
                        <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{fmtDate(o.createdAt)}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                          {fmtUGX(o.totalAmount)}
                        </p>
                        <span className={o.status === 'PAID' ? 'badge badge-green' : 'badge badge-gray'}>
                          {o.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Loyalty transactions */}
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                Loyalty Transactions ({loyaltyTxs.length})
              </p>
              {loyaltyTxs.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No loyalty transactions.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {loyaltyTxs.map((tx, idx) => (
                    <div
                      key={tx.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingTop: 10,
                        paddingBottom: 10,
                        borderBottom: idx < loyaltyTxs.length - 1 ? '1px solid var(--border)' : 'none',
                      }}
                    >
                      <div>
                        <p style={{ fontSize: 12, color: 'var(--text-3)' }}>{tx.description ?? tx.type}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{fmtDate(tx.createdAt)}</p>
                      </div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: tx.points >= 0 ? 'var(--green)' : 'var(--red)', fontFamily: 'var(--font-mono)' }}>
                        {tx.points >= 0 ? '+' : ''}{tx.points}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: 'var(--text-3)' }}>Customer not found.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Campaign modal ────────────────────────────────────────────────────────────

const CAMPAIGN_TYPES = [
  { value: 'birthday', label: 'Birthday' },
  { value: 'win_back', label: 'Win Back' },
  { value: 'new_menu', label: 'New Menu' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'loyalty_milestone', label: 'Loyalty Milestone' },
  { value: 'manual', label: 'Manual' },
] as const;

function CampaignModal({
  onClose,
  onSave,
  isSaving,
}: {
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [campaignType, setCampaignType] = useState<string>('manual');
  const [marketingOptInOnly, setMarketingOptInOnly] = useState(true);
  const [scheduledAt, setScheduledAt] = useState('');

  const canSave = name.trim().length > 0 && messageTemplate.trim().length > 0;

  function handleSave() {
    if (!canSave) return;
    const payload: Record<string, unknown> = {
      name: name.trim(),
      messageTemplate: messageTemplate.trim(),
      campaignType,
      audienceFilter: { marketingOptIn: marketingOptInOnly },
    };
    if (scheduledAt) payload['scheduledAt'] = scheduledAt;
    onSave(payload);
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2 className="modal-title">New Campaign</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="field">
            <label className="field-label">Campaign Name *</label>
            <input
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. June Promotion"
            />
          </div>
          <div className="field">
            <label className="field-label">Type</label>
            <select className="input" value={campaignType} onChange={e => setCampaignType(e.target.value)}>
              {CAMPAIGN_TYPES.map(ct => (
                <option key={ct.value} value={ct.value}>{ct.label}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Message Template *</label>
            <textarea
              className="input"
              value={messageTemplate}
              onChange={e => setMessageTemplate(e.target.value)}
              rows={4}
              placeholder="Hi {name}, we have a special offer just for you…"
              style={{ resize: 'none' }}
            />
            <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
              Use <code style={{ background: 'var(--surface2)', padding: '1px 4px', borderRadius: 4 }}>{'{name}'}</code> to personalise with the customer&apos;s name.
            </p>
          </div>
          <div className="field">
            <label className="field-label">Schedule (optional)</label>
            <input
              className="input"
              type="datetime-local"
              value={scheduledAt}
              onChange={e => setScheduledAt(e.target.value)}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div
              onClick={() => setMarketingOptInOnly(v => !v)}
              style={{
                position: 'relative',
                display: 'inline-flex',
                width: 36,
                height: 20,
                alignItems: 'center',
                borderRadius: 999,
                background: marketingOptInOnly ? 'var(--accent)' : 'var(--surface2)',
                cursor: 'pointer',
                transition: 'background 0.2s',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  transform: marketingOptInOnly ? 'translateX(18px)' : 'translateX(3px)',
                  transition: 'transform 0.2s',
                }}
              />
            </div>
            <span style={{ fontSize: 13, color: 'var(--text-2)' }}>Send only to opted-in customers</span>
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={!canSave || isSaving}>
            {isSaving ? 'Creating…' : 'Create Campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function CrmPage() {
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState<'customers' | 'campaigns'>('customers');
  const [search, setSearch] = useState('');
  const [customerModal, setCustomerModal] = useState<{ open: boolean; editing?: Customer }>({ open: false });
  const [historyCustomerId, setHistoryCustomerId] = useState<string | null>(null);
  const [campaignModal, setCampaignModal] = useState(false);

  // ── Customers query ──────────────────────────────────────────────────────────
  const { data: customers = [], isLoading: customersLoading } = useQuery<Customer[]>({
    queryKey: ['crm-customers', search],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50', offset: '0', search });
      const res = await fetch(`${API_BASE}/v1/crm/customers?${params.toString()}`, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data ?? [];
    },
  });

  // ── Campaigns query ──────────────────────────────────────────────────────────
  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery<Campaign[]>({
    queryKey: ['crm-campaigns'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/crm/campaigns`, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data ?? [];
    },
  });

  // ── Mutations ────────────────────────────────────────────────────────────────

  const saveCustomer = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: Record<string, unknown> }) => {
      const res = await fetch(
        id ? `${API_BASE}/v1/crm/customers/${id}` : `${API_BASE}/v1/crm/customers`,
        { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-customers'] });
      setCustomerModal({ open: false });
    },
  });

  const createCampaign = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await fetch(`${API_BASE}/v1/crm/campaigns`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(data),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Create failed');
      return j.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-campaigns'] });
      setCampaignModal(false);
    },
  });

  const sendCampaign = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/v1/crm/campaigns/${id}/send`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Send failed');
      return j.data as { sent: number; failed: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-campaigns'] });
    },
  });

  const sendBirthdaySms = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/v1/crm/birthday-sms`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Birthday SMS failed');
      return j.data as { sent: number };
    },
  });

  // ── Derived stats ────────────────────────────────────────────────────────────
  const optedInCount = customers.filter(c => c.marketingOptIn).length;
  const draftCampaigns = campaigns.filter(c => c.status === 'draft').length;

  // ── Helpers ──────────────────────────────────────────────────────────────────
  function campaignStatusClass(status: Campaign['status']): string {
    if (status === 'sent') return 'badge badge-green';
    if (status === 'failed') return 'badge badge-red';
    return 'badge badge-amber';
  }

  function campaignTypeLabel(type: string): string {
    return CAMPAIGN_TYPES.find(ct => ct.value === type)?.label ?? type;
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {customers.length} customers · {optedInCount} opted-in · {draftCampaigns} draft campaigns
          </p>
        </div>
        {activeTab === 'customers' && (
          <button className="btn btn-primary" onClick={() => setCustomerModal({ open: true })}>
            + Customer
          </button>
        )}
        {activeTab === 'campaigns' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="btn btn-ghost"
              style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
              onClick={() => sendBirthdaySms.mutate()}
              disabled={sendBirthdaySms.isPending}
            >
              {sendBirthdaySms.isPending ? 'Sending…' : 'Birthday SMS'}
            </button>
            <button className="btn btn-primary" onClick={() => setCampaignModal(true)}>
              + Campaign
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {(['customers', 'campaigns'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '6px 16px',
              borderRadius: 7,
              border: 'none',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              background: activeTab === tab ? 'var(--surface)' : 'transparent',
              color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
              boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {tab === 'customers' ? 'Customers' : 'Campaigns'}
          </button>
        ))}
      </div>

      {/* ── Customers tab ────────────────────────────────────────────────────── */}
      {activeTab === 'customers' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <input
              className="input"
              style={{ maxWidth: 320 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or phone…"
            />
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {customersLoading ? (
              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ height: 56, background: 'var(--surface2)', borderRadius: 8, animation: 'pulse 1.5s infinite' }} />
                ))}
              </div>
            ) : customers.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-3)' }}>
                  {search ? 'No customers match your search.' : 'No customers yet.'}
                </p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th style={{ textAlign: 'center' }}>Tier</th>
                      <th style={{ textAlign: 'right' }}>Points</th>
                      <th style={{ textAlign: 'right' }}>Total Spend</th>
                      <th style={{ textAlign: 'right' }}>Visits</th>
                      <th>Last Visit</th>
                      <th style={{ textAlign: 'center' }}>Opt-In</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map(c => (
                      <tr key={c.id}>
                        <td>
                          <p style={{ fontWeight: 500, color: 'var(--text)' }}>{c.fullName}</p>
                          {c.email && <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{c.email}</p>}
                          {c.isBlacklisted && <span className="badge badge-red">Blacklisted</span>}
                        </td>
                        <td style={{ color: 'var(--text-2)' }}>{c.phone}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={tierBadgeClass(c.loyaltyTier)}>{c.loyaltyTier}</span>
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                          {c.loyaltyPoints.toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                          {fmtUGX(c.totalSpendUgx)}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{c.visitCount}</td>
                        <td style={{ color: 'var(--text-3)', fontSize: 12 }}>{fmtDate(c.lastVisitDate)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: c.marketingOptIn ? 'var(--green)' : 'var(--text-3)',
                            }}
                            title={c.marketingOptIn ? 'Opted in' : 'Opted out'}
                          />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: 'var(--accent)' }}
                              onClick={() => setHistoryCustomerId(c.id)}
                            >
                              View
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => setCustomerModal({ open: true, editing: c })}
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Campaigns tab ────────────────────────────────────────────────────── */}
      {activeTab === 'campaigns' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {campaignsLoading ? (
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} style={{ height: 64, background: 'var(--surface2)', borderRadius: 8, animation: 'pulse 1.5s infinite' }} />
              ))}
            </div>
          ) : campaigns.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No campaigns yet. Create your first one.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th>Sent At</th>
                    <th style={{ textAlign: 'right' }}>Recipients</th>
                    <th style={{ textAlign: 'right' }}>Delivered</th>
                    <th style={{ textAlign: 'right' }}>Failed</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(camp => (
                    <tr key={camp.id}>
                      <td>
                        <p style={{ fontWeight: 500, color: 'var(--text)' }}>{camp.name}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-3)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {camp.messageTemplate}
                        </p>
                      </td>
                      <td style={{ color: 'var(--text-2)' }}>{campaignTypeLabel(camp.campaignType)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={campaignStatusClass(camp.status)}>{camp.status}</span>
                      </td>
                      <td style={{ color: 'var(--text-3)', fontSize: 12 }}>{fmtDate(camp.sentAt)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{camp.recipientCount}</td>
                      <td style={{ textAlign: 'right', color: 'var(--green)', fontWeight: 500 }}>{camp.deliveredCount}</td>
                      <td style={{ textAlign: 'right', color: 'var(--red)', fontWeight: 500 }}>{camp.failedCount}</td>
                      <td style={{ textAlign: 'right' }}>
                        {camp.status === 'draft' && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: 'var(--accent)' }}
                            onClick={() => sendCampaign.mutate(camp.id)}
                            disabled={sendCampaign.isPending}
                          >
                            {sendCampaign.isPending ? '…' : 'Send'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      {customerModal.open && (
        <CustomerModal
          initial={customerModal.editing}
          onClose={() => setCustomerModal({ open: false })}
          onSave={data => saveCustomer.mutate({ id: customerModal.editing?.id, data })}
          isSaving={saveCustomer.isPending}
        />
      )}

      {campaignModal && (
        <CampaignModal
          onClose={() => setCampaignModal(false)}
          onSave={data => createCampaign.mutate(data)}
          isSaving={createCampaign.isPending}
        />
      )}

      {historyCustomerId && (
        <CustomerHistoryPanel
          customerId={historyCustomerId}
          onClose={() => setHistoryCustomerId(null)}
        />
      )}
    </div>
  );
}
